/**
 * DynamicFieldRenderer
 *
 * Renders dynamic form fields based on field-type configuration via switch dispatch.
 * Reviewed during SPLIT-07 refactoring (Phase 6). Retained as single file: 234 lines of
 * pure switch dispatch is the component's single responsibility. Each field-type case is
 * 8-15 lines; splitting into 9 files + router would increase complexity without benefit.
 */
import PropTypes from 'prop-types';
import { startCase } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { Box, FormControlLabel, InputAdornment, MenuItem, Radio, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import NgpHostnameField from '../../../../../form/NgpHostnameField';
import NgpNativeNumberField from '../../../../../form/NgpNativeNumberField';
import NgpSelect from '../../../../../form/NgpSelect';
import NgpRadioGroup from '../../../../../form/NgpRadioGroup';
import NgpToggle from '../../../../../form/NgpToggle';
import NgpPasswordField from '../../../../../form/NgpPasswordField';
import NgpTextField from '../../../../../form/NgpTextField';
import NgpFieldHelpTip from '../../../../../form/NgpFieldHelpTip';
import { FIELD_TYPE } from './DynamicConfigConstants';
import { RequestHeaderBuilderPopover } from '../../../../../panels/web-test-now-panels/RequestHeaderBuilderPopover';
import { SHOULD_DIRTY_TRUE } from '../../../ServiceTestsFormControlConstants';

const FIELD_LARGE_WIDTH = 640;
const FIELD_LONG_WIDTH = 500;
const FIELD_MIN_WIDTH = 180;
const MIN_FULL_WIDTH_LENGTH = 256;

// Webex URL (https://[corporate-domain].webex.com/ or https://webex.com/), 72, isLargeWidth=true
// Enable or Disable Forms Login Methods (comma separated filter list),     70, isLargeWidth=false
const MIN_LARGE_WIDTH_OF_LABEL_LENGTH = 70;

const useStyles = makeStyles((theme) => ({
    // Consistent width for all dynamic input fields
    dynamicField: {
        '& .MuiSelect-select': {
            minWidth: FIELD_LONG_WIDTH,
        },
        '& .MuiInputBase-input': {
            minWidth: FIELD_LONG_WIDTH,
        },
    },
    dynamicFieldLongWidth: {
        '& .MuiSelect-select': {
            minWidth: FIELD_LONG_WIDTH,
        },
        '& .MuiInputBase-input': {
            minWidth: FIELD_LONG_WIDTH,
        },
    },
    dynamicFieldLargeWidth: {
        '& .MuiInputBase-input': {
            width: FIELD_LARGE_WIDTH,
        },
    },
    passwordField: {
        '& .MuiInputBase-input': {
            minWidth: FIELD_MIN_WIDTH,
        },
    },
    numberField: {
        '& .MuiInputBase-input': {
            minWidth: FIELD_MIN_WIDTH,
        },
    },
    // Wrapper for field + units + help icon group on the same line
    fieldWithAdornment: {
        display: 'flex',
        alignItems: 'flex-end',
        // Preserve NgpRow-style gap between this group and the next field in the row
        marginRight: theme.spacing(8),
        '& .MuiTextField-root': {
            // Remove internal gap between field and units/tooltip
            marginRight: 0,
            minWidth: FIELD_MIN_WIDTH,
        },
        '& .MuiFormControlLabel-root': {
            marginTop: theme.spacing(0.125),
        },
    },
    boxMarginTop: {
        marginTop: theme.spacing(3),
    },
}));

const DEFAULT_TEXT_FIELD_MAX_LENGTH = 75;
const DEFAULT_PASSWORD_FIELD_MAX_LENGTH = 50;

/**
 * Derives a human-readable label from the field config.
 * Uses field.label if defined, otherwise converts field.title from snake_case.
 * e.g. 'port_max' → 'Port Max', 'packet_size' → 'Packet Size'
 */
const getFieldLabel = (field) => field.label || startCase(field.title);

// For token field(field?.maxlength) and long label field(label?.length ), show large width
const isLargeWidth = (field, label) =>
    (field?.maxlength && field?.maxlength > MIN_FULL_WIDTH_LENGTH) ||
    (label?.length && label?.length > MIN_LARGE_WIDTH_OF_LABEL_LENGTH);

/**
 * Renders a single dynamic form field based on the field config object
 * from testType.testBuilder.details.sections.
 *
 * @param {object} props
 * @param {object} props.field - Field config: { title, type, label, required, default, min, max, options, ... }
 * @param {Array} [props.ssidList] - WiFi network profiles (for 'wi-fi networks' fields)
 * @param {Array} [props.wifiTestList] - WiFi test list (for 'wi-fi tests' fields)
 */
export default function DynamicFieldRenderer({ field, ssidList, wifiTestList }) {
    const { watch, setValue } = useFormContext();
    const classes = useStyles();
    // Password fields bind to .value sub-path to produce {value: "userInput"} object structure.
    // portal/ui template: ng-model="details.test.testConfig[field.title].value"
    // Backend expects ScrubbedEncryptedItem (embedded object), not a plain string.
    const dynamicFieldName =
        field.type === FIELD_TYPE.PASSWORD ? `testConfig.${field.title}.value` : `testConfig.${field.title}`;
    const label = getFieldLabel(field);

    // Conditional visibility: hide field when showField value doesn't match showValue.
    // Use loose string comparison to handle boolean/number/string mismatches from API config.
    if (field.showField) {
        const watchedValue = watch(`testConfig.${field.showField}`);
        if (String(watchedValue) !== String(field.showValue)) {
            return null;
        }
    }

    const renderFieldByType = () => {
        switch (field.type) {
            case FIELD_TYPE.HOSTNAME:
                return (
                    <NgpHostnameField
                        fieldName={dynamicFieldName}
                        label={label}
                        required={field.required}
                        disabled={field.disabled}
                        className={classes.dynamicFieldLongWidth}
                        inputProps={{ maxLength: field?.maxlength || DEFAULT_TEXT_FIELD_MAX_LENGTH }}
                    />
                );

            case FIELD_TYPE.NUMBER:
                return (
                    <NgpNativeNumberField
                        fieldName={dynamicFieldName}
                        label={label}
                        required={field.required}
                        disabled={field.disabled}
                        className={classes.numberField}
                        min={field.min}
                        max={field.max}
                        step="any"
                    />
                );

            case FIELD_TYPE.INTEGER:
                return (
                    <NgpNativeNumberField
                        fieldName={dynamicFieldName}
                        label={label}
                        required={field.required}
                        disabled={field.disabled}
                        className={classes.numberField}
                        min={field.min}
                        max={field.max}
                        step={1}
                    />
                );

            case FIELD_TYPE.SELECT:
                return (
                    <NgpSelect
                        fieldName={dynamicFieldName}
                        label={label}
                        required={field.required}
                        disabled={field.disabled}
                        className={classes.dynamicField}
                    >
                        {(field.options || []).map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </NgpSelect>
                );

            case FIELD_TYPE.RADIO:
                return (
                    <Box className={classes.boxMarginTop}>
                        <Typography variant="body1">{label}</Typography>
                        <NgpRadioGroup fieldName={dynamicFieldName} valueType="string">
                            {(field.options || []).map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={<Radio />}
                                    label={option.name}
                                />
                            ))}
                        </NgpRadioGroup>
                    </Box>
                );

            case FIELD_TYPE.BOOLEAN:
                return (
                    <Box className={classes.boxMarginTop}>
                        <NgpToggle fieldName={dynamicFieldName} label={label} />
                    </Box>
                );

            case FIELD_TYPE.PASSWORD:
                return (
                    <NgpPasswordField
                        fieldName={dynamicFieldName}
                        label={label}
                        required={field.required}
                        disabled={field.disabled}
                        className={classes.passwordField}
                        inputProps={{ maxLength: field?.maxlength || DEFAULT_PASSWORD_FIELD_MAX_LENGTH }}
                        togglePasswordVisibility
                    />
                );

            case FIELD_TYPE.WIFI_NETWORKS:
                return (
                    <NgpSelect
                        fieldName={dynamicFieldName}
                        label={label}
                        required={field.required}
                        disabled={field.disabled}
                        className={classes.dynamicField}
                    >
                        {(ssidList || []).map((network) => (
                            <MenuItem key={network._id} value={network._id}>
                                {network.profileName}
                            </MenuItem>
                        ))}
                    </NgpSelect>
                );

            case FIELD_TYPE.WIFI_TESTS:
                return (
                    <NgpSelect
                        fieldName={dynamicFieldName}
                        label={label}
                        required={field.required}
                        disabled={field.disabled}
                        className={classes.dynamicField}
                    >
                        {(wifiTestList || []).map((test) => (
                            <MenuItem key={test._id || 'no-test'} value={test._id}>
                                {test.name}
                            </MenuItem>
                        ))}
                    </NgpSelect>
                );

            case FIELD_TYPE.TEXT:
            default: {
                // Check if this is a user-agent field (case-insensitive, matches RequestHeaderValueField logic)
                const isUserAgent = String(field.title || '').toLowerCase() === 'user-agent';

                const handleApplyUserAgent = (ua) => {
                    setValue(dynamicFieldName, ua, SHOULD_DIRTY_TRUE);
                };

                const fieldLargeWidth = isLargeWidth(field, label);
                return (
                    <NgpTextField
                        fieldName={dynamicFieldName}
                        label={label}
                        required={field.required}
                        disabled={field.disabled}
                        className={fieldLargeWidth ? classes.dynamicFieldLargeWidth : classes.dynamicFieldLongWidth}
                        inputProps={{ maxLength: field?.maxlength || DEFAULT_TEXT_FIELD_MAX_LENGTH }}
                        /* eslint-disable-next-line react/jsx-no-duplicate-props */
                        InputProps={
                            isUserAgent
                                ? {
                                      endAdornment: (
                                          <InputAdornment position="end">
                                              <RequestHeaderBuilderPopover onApply={handleApplyUserAgent} />
                                          </InputAdornment>
                                      ),
                                  }
                                : undefined
                        }
                    />
                );
            }
        }
    };

    const fieldElement = renderFieldByType();

    // Wrap with units display (pattern from ThresholdMetricRow.jsx)
    if (field.units) {
        return (
            <div className={classes.fieldWithAdornment}>
                {fieldElement}
                <Box ml={1}>
                    <Typography variant="body2">{field.units}</Typography>
                </Box>
                {field.helpTooltip && (
                    <Box ml={1}>
                        <NgpFieldHelpTip helpText={field.helpTooltip} />
                    </Box>
                )}
            </div>
        );
    }

    // Wrap with helpTooltip only
    if (field.helpTooltip) {
        return (
            <div className={classes.fieldWithAdornment}>
                {fieldElement}
                <Box ml={1}>
                    <NgpFieldHelpTip helpText={field.helpTooltip} />
                </Box>
            </div>
        );
    }

    return <div className={classes.fieldWithAdornment}>{fieldElement}</div>;
}

DynamicFieldRenderer.propTypes = {
    field: PropTypes.object.isRequired,
    ssidList: PropTypes.array,
    wifiTestList: PropTypes.array,
};

DynamicFieldRenderer.defaultProps = {
    ssidList: [],
    wifiTestList: [],
};
